/**
 * Leads capturados en el storefront (popup primer ingreso).
 * Descarga CSV — sin envío de marketing todavía.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { Download, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ClinicalPageShell } from "@/components/clinical";
import { DataTable, EmptyState, Loading, CrudShellHeader } from "@/components/ds";
import type { DataTableColumn } from "@/components/ds";
import { LeadsTour } from "@/components/ProductTour";
import { apiGetJson, clientRequestHeaders } from "@/lib/sm-api";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { toast } from "sonner";

export const Route = createFileRoute("/app/leads")({
  component: LeadsPage,
  head: () => ({ meta: [{ title: "Prospectos — Structura Market" }] }),
});

type Lead = {
  id: string;
  email: string;
  name: string | null;
  birthDate: string | null;
  source: string;
  createdAt: string;
};

function LeadsPage() {
  const tid = useWorkspaceTenantId();
  const [leads, setLeads] = useState<Lead[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!tid) return;
    setError(null);
    try {
      const res = await apiGetJson<{ leads: Lead[]; total: number }>("/api/tenant/leads", tid);
      setLeads(res.leads);
    } catch (e) {
      setLeads([]);
      setError(e instanceof Error ? e.message : "No se pudieron cargar los leads");
    }
  }, [tid]);

  useEffect(() => {
    void load();
  }, [load]);

  const downloadCsv = async () => {
    if (!tid) return;
    try {
      const res = await fetch("/api/tenant/leads?format=csv", {
        headers: clientRequestHeaders(tid),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error || `Error ${res.status}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `leads-${tid}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV descargado");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "No se pudo descargar");
    }
  };

  const columns: DataTableColumn<Lead>[] = [
    { key: "email", header: "Email", render: (r) => r.email },
    { key: "name", header: "Nombre", render: (r) => r.name || "—" },
    { key: "birthDate", header: "Nacimiento", render: (r) => r.birthDate || "—" },
    {
      key: "createdAt",
      header: "Fecha",
      render: (r) => new Date(r.createdAt).toLocaleString("es-AR"),
    },
  ];

  return (
    <div>
      <LeadsTour />
      <CrudShellHeader
        title="Prospectos"
        description="Contactos que dejaron su email en la tienda. Todavía no compraron."
        backHref="/app/clientes"
        backLabel="Volver a Clientes"
        action={
          <Button type="button" data-tour="leads-csv" onClick={() => void downloadCsv()} disabled={!leads?.length}>
            <Download className="mr-2 h-4 w-4" />
            Descargar CSV
          </Button>
        }
      />
      <ClinicalPageShell offset="none" className="pt-6">

      {error ? <p className="mb-4 text-sm text-destructive">{error}</p> : null}

      <div data-tour="leads-table">
        {leads === null ? (
          <Loading />
        ) : leads.length === 0 ? (
          <EmptyState
            icon={Mail}
            title="Todavía no hay prospectos"
            description="Cuando alguien deje su email en la tienda, va a aparecer acá."
          />
        ) : (
          <DataTable columns={columns} rows={leads} getRowKey={(r) => r.id} />
        )}
      </div>
    </ClinicalPageShell>
    </div>
  );
}
